import moment from 'moment';

export type BidReminderPreset = 'halfway' | 'sevenDaysBefore' | 'oneDayBefore' | 'custom';

export type BidReminderChoice = { preset: BidReminderPreset | 'none'; date?: string };

export const REMINDER_PRESETS: BidReminderPreset[] = [
  'halfway',
  'sevenDaysBefore',
  'oneDayBefore',
  'custom',
];

export const REMINDER_OPTIONS: BidReminderChoice['preset'][] = ['none', ...REMINDER_PRESETS];

const ISO_DAY = 'YYYY-MM-DD';

const startOfDay = (date: Date) => moment(date).startOf('day');

// Local date parts: toISOString would shift a Stockholm evening pick to the previous day.
export const toIsoDay = (day: Date): string => moment(day).format(ISO_DAY);

export const fromIsoDay = (day: string): Date => moment(day, ISO_DAY, true).toDate();

export const reminderWindow = (deadline: Date, today: Date) => ({
  first: startOfDay(today).add(1, 'day').toDate(),
  last: startOfDay(deadline).subtract(1, 'day').toDate(),
});

export const isSelectableReminderDay = (day: Date, deadline: Date, today: Date): boolean => {
  const { first, last } = reminderWindow(deadline, today);
  const candidate = startOfDay(day);
  return !candidate.isBefore(first) && !candidate.isAfter(last);
};

export const presetReminderDay = (
  preset: BidReminderPreset | 'none',
  deadline: Date,
  today: Date,
): Date | undefined => {
  const deadlineDay = startOfDay(deadline);
  const todayDay = startOfDay(today);
  switch (preset) {
    case 'halfway':
      return todayDay.add(Math.floor(deadlineDay.diff(todayDay, 'days') / 2), 'days').toDate();
    case 'sevenDaysBefore':
      return deadlineDay.subtract(7, 'days').toDate();
    case 'oneDayBefore':
      return deadlineDay.subtract(1, 'day').toDate();
    default:
      return undefined;
  }
};

export const isPresetSelectable = (
  preset: BidReminderPreset | 'none',
  deadline: Date,
  today: Date,
): boolean => {
  if (preset === 'none') return true;
  if (preset === 'custom') {
    const { first, last } = reminderWindow(deadline, today);
    return first <= last;
  }
  const day = presetReminderDay(preset, deadline, today);
  return !!day && isSelectableReminderDay(day, deadline, today);
};

export const resolveReminderDay = (
  choice: BidReminderChoice,
  deadline: Date,
  today: Date,
): string | undefined => {
  if (choice.preset === 'none') return undefined;
  const day =
    choice.preset === 'custom'
      ? choice.date
        ? fromIsoDay(choice.date)
        : undefined
      : presetReminderDay(choice.preset, deadline, today);
  if (!day || !isSelectableReminderDay(day, deadline, today)) return undefined;
  return toIsoDay(day);
};

export const toReminderPayload = (
  choice: BidReminderChoice,
  deadline: Date,
  today: Date,
): { preset: BidReminderPreset; date: string } | undefined => {
  if (choice.preset === 'none') return undefined;
  const date = resolveReminderDay(choice, deadline, today);
  return date ? { preset: choice.preset, date } : undefined;
};
